import { memo, useDeferredValue, useEffect, useMemo, useState, type ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import { ParamApply, ParamSource, ParamType, type ParamValue } from '@gr/core/platform/config/registry';
import { SettingWarning, type SettingDto } from '@gr/core/platform/config/dto';
import { useT } from '../../../../app/di';
import { formatDateTime } from '../../../../shared/format';
import { Accordion, AccordionSection } from '../../../../ui-kit/Accordion/Accordion';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { BareInput, BareSelect, Checkbox } from '../../../../ui-kit/Field/Field';
import { Switch } from '../../../../ui-kit/Switch/Switch';
import {
  buildSettingsIndex,
  filterSettings,
  initiallyOpenSections,
  inputText,
  isFiltering,
  isToggle,
  parseSettingInput,
  unitOf,
  type SettingsSection,
} from '../model/platform-settings';
import { docRefLines, splitDocRefs } from '../../../../shared/config/doc-refs';
import { InfoTip } from '../../../../ui-kit/InfoTip/InfoTip';
import styles from './PlatformSettings.module.scss';

/** Чем закончилось сохранение или сброс: предупреждения (CFG-UC-2.CC-1, CFG-UC-2.CC-2) или коды ошибок. */
export type SettingOutcome = { ok: true; warnings: SettingWarning[] } | { ok: false; errors: string[] };

export interface PlatformSettingsProps {
  settings: readonly SettingDto[];
  onSave(key: string, value: ParamValue): Promise<SettingOutcome>;
  /** Вернуть к окружению или умолчанию. */
  onReset(key: string): Promise<SettingOutcome>;
  /** DEMO-RULE-4: демо-режим — только просмотр, без кнопок. */
  readOnly?: boolean;
}

/**
 * CFG-UC-2, CFG-UC-3: параметры по группам (основные, второстепенные) и сворачиваемым разделам —
 * значение, источник, «ждёт перезапуска», правка и сброс; функции платформы — переключателем.
 * Быстрый поиск идёт по индексу, собранному один раз на загруженный список (ADR-017).
 */
export function PlatformSettings({ settings, onSave, onReset, readOnly = false }: PlatformSettingsProps): ReactElement {
  const { t } = useT();
  const [query, setQuery] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const index = useMemo(() => buildSettingsIndex(settings), [settings]);
  // Поле ввода не ждёт перерисовки списка: фильтр догоняет его, когда браузер свободен.
  const deferredQuery = useDeferredValue(query);
  const filter = useMemo(() => ({ query: deferredQuery, onlyChanged }), [deferredQuery, onlyChanged]);
  const view = useMemo(() => filterSettings(index, filter), [index, filter]);
  const filtering = isFiltering(filter);

  // CFG-UC-2.CC-6: основные раскрыты, второстепенные свёрнуты; пока идёт поиск, раскрыто всё найденное,
  // а свёрнутое в поиске забывается при новом запросе.
  const [open, setOpen] = useState<ReadonlySet<string> | null>(null);
  const [closedInSearch, setClosedInSearch] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => setClosedInSearch(new Set()), [filter]);
  const opened = open ?? initiallyOpenSections(index);
  const isOpen = (section: SettingsSection): boolean => (filtering ? !closedInSearch.has(section.id) : opened.has(section.id));
  const toggle = (section: SettingsSection, next: boolean): void => {
    const change = (current: ReadonlySet<string>, add: boolean): Set<string> => {
      const copy = new Set(current);
      if (add) copy.add(section.id);
      else copy.delete(section.id);
      return copy;
    };
    if (filtering) setClosedInSearch((current) => change(current, !next));
    else setOpen(change(opened, next));
  };

  return (
    <div className="u-stack">
      {readOnly ? (
        <Alert tone="info" title={t('config.readOnlyTitle')}>
          {t('config.readOnly')}
        </Alert>
      ) : null}
      <div className={styles.filters}>
        <div className={styles.search}>
          <BareInput label={t('config.search')} placeholder={t('config.searchHint')} type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <Checkbox label={t('config.onlyChanged')} checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
      </div>
      <p className="u-muted u-small" aria-live="polite">
        {t('config.found', { shown: view.shown, total: view.total })}
      </p>
      {view.groups.length === 0 ? (
        <EmptyState title={t('config.nothingFound')} />
      ) : (
        view.groups.map(({ group, sections }) => (
          <section key={group} className="u-stack" aria-labelledby={`settings-group-${group}`}>
            <h2 id={`settings-group-${group}`} className={styles.groupTitle}>
              {t(`config.groups.${group}`)}
            </h2>
            <Accordion label={t(`config.groups.${group}`)}>
              {sections.map((section) => (
                <AccordionSection
                  key={section.id}
                  title={t(`config.sections.${section.name}`)}
                  meta={String(section.settings.length)}
                  open={isOpen(section)}
                  onToggle={(next) => toggle(section, next)}
                >
                  <ul className={styles.list}>
                    {section.settings.map((setting) => (
                      <SettingRow key={setting.key} setting={setting} onSave={onSave} onReset={onReset} readOnly={readOnly} />
                    ))}
                  </ul>
                </AccordionSection>
              ))}
            </Accordion>
          </section>
        ))
      )}
    </div>
  );
}

const SOURCE_TONES = { [ParamSource.Default]: 'neutral', [ParamSource.Env]: 'secondary', [ParamSource.Saved]: 'accent' } as const;

interface SettingRowProps {
  setting: SettingDto;
  onSave: PlatformSettingsProps['onSave'];
  onReset: PlatformSettingsProps['onReset'];
  readOnly: boolean;
}

/** Строка перерисовывается, только когда меняется её параметр: фильтр не трогает остальные. */
const SettingRow = memo(function SettingRow({ setting, onSave, onReset, readOnly }: SettingRowProps): ReactElement {
  const { t, te, locale } = useT();
  const [draft, setDraft] = useState<string | boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SettingOutcome | null>(null);
  const editing = draft !== null;
  const unit = unitOf(setting.key);
  // CFG-UC-3, CFG-UC-3.CC-1: функция платформы и другой логический параметр — переключатель, который сохраняет сразу.
  const toggle = isToggle(setting) && !readOnly;

  const text = (value: ParamValue | null): string => {
    if (setting.secret) return setting.hasValue ? t('config.secretSet') : t('config.notSet');
    if (value === null || value === '') return t('config.notSet');
    if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
    const plain = inputText(value);
    return unit ? `${plain} ${t(`config.units.${unit}`)}` : plain;
  };

  const run = async (action: () => Promise<SettingOutcome>): Promise<void> => {
    setBusy(true);
    const result = await action();
    setBusy(false);
    setOutcome(result);
    if (result.ok) setDraft(null);
  };

  const save = (): void => {
    if (draft === null) return;
    const value = parseSettingInput(setting, draft);
    if (value === null) {
      setOutcome({ ok: false, errors: [ErrorCode.ConfigValueInvalid] });
      return;
    }
    void run(() => onSave(setting.key, value));
  };

  const bound = (value: number | null): string => (value === null ? '—' : String(value));
  const range =
    setting.type === ParamType.IntegerList
      ? t('config.listRange', { min: bound(setting.min), max: bound(setting.max), minItems: bound(setting.minItems), maxItems: bound(setting.maxItems) })
      : setting.min !== null
        ? t('config.range', { min: bound(setting.min), max: bound(setting.max) })
        : null;

  // CFG-RULE-2: коды документации — в подсказке «i» после подписи, а не в самой подписи.
  const description = splitDocRefs(setting.description[locale]);

  return (
    <li className={styles.row} data-setting={setting.key}>
      <div className={styles.head}>
        <div className={styles.title}>
          <span>
            {description.text}
            {description.refs.length > 0 ? (
              <>
                {' '}
                <InfoTip label={t('config.docRefs.label')} heading={t('config.docRefs.heading')} lines={docRefLines(description.refs, t)} />
              </>
            ) : null}
          </span>
          <code className={styles.key}>{setting.key}</code>
        </div>
        <div className={styles.badges}>
          <Badge tone={SOURCE_TONES[setting.source]}>{t(`config.sources.${setting.source}`)}</Badge>
          {setting.apply === ParamApply.Restart ? <Badge>{t('config.restartApply')}</Badge> : null}
          {setting.personal ? <Badge tone="secondary">{t('config.personal')}</Badge> : null}
          {setting.pendingRestart ? <Badge tone="warning">{t('config.pendingRestart')}</Badge> : null}
          {!setting.editable ? <Badge tone="secondary">{t('config.envOnly')}</Badge> : null}
          {setting.secret ? <Badge tone="secondary">{t('config.secret')}</Badge> : null}
        </div>
      </div>

      {toggle ? (
        <Switch
          label={setting.value === true ? t('config.on') : t('config.off')}
          aria-label={description.text}
          checked={setting.value === true}
          disabled={busy}
          onChange={(checked) => {
            setOutcome(null);
            void run(() => onSave(setting.key, checked));
          }}
        />
      ) : editing ? (
        <div className={styles.editor}>
          <SettingEditor setting={setting} draft={draft} onChange={setDraft} disabled={busy} />
          {range ? <p className="u-muted u-small">{range}</p> : null}
        </div>
      ) : (
        <p className={styles.value}>{text(setting.value)}</p>
      )}

      <dl className={styles.details}>
        {setting.pendingRestart ? <Detail term={t('config.afterRestart')} value={text(setting.pendingValue)} /> : null}
        {setting.envValue !== null && setting.source === ParamSource.Saved ? <Detail term={t('config.envOverridden', { env: setting.env ?? '' })} value={text(setting.envValue)} /> : null}
        {setting.env && setting.source === ParamSource.Env ? <Detail term={t('config.envName')} value={setting.env} /> : null}
        {setting.updatedAt && setting.savedValue !== null ? (
          <Detail term={t('config.saved')} value={t('config.savedBy', { value: text(setting.savedValue), login: setting.updatedBy ?? '—', at: formatDateTime(setting.updatedAt, locale) })} />
        ) : null}
        {setting.source !== ParamSource.Default && setting.defaultValue !== null ? <Detail term={t('config.defaultValue')} value={text(setting.defaultValue)} /> : null}
      </dl>
      {setting.savedInvalid ? <p className="u-text-danger u-small">{t('config.savedInvalid')}</p> : null}
      {!setting.editable ? <p className="u-muted u-small">{t('config.envOnlyHint', { env: setting.env ?? '' })}</p> : null}
      {setting.personal ? <p className="u-muted u-small">{t('config.personalHint')}</p> : null}

      {outcome && !outcome.ok ? (
        <Alert tone="danger" title={t('config.notSaved')}>
          {te(outcome.errors)}
        </Alert>
      ) : null}
      {outcome?.ok && outcome.warnings.length > 0 ? (
        <Alert tone="warning" title={t('config.savedWithWarnings')}>
          <ul className="u-bullets">
            {outcome.warnings.map((warning) => (
              <li key={warning}>{t(`config.warnings.${warning}`, { env: setting.env ?? '' })}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {setting.editable && !readOnly ? (
        <div className={styles.actions}>
          {toggle ? null : editing ? (
            <>
              <Button variant="primary" size="sm" icon="check" loading={busy} onClick={save}>
                {t('common.save')}
              </Button>
              <Button size="sm" disabled={busy} onClick={() => setDraft(null)}>
                {t('common.cancel')}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              icon="edit"
              onClick={() => {
                setOutcome(null);
                setDraft(inputText(setting.value));
              }}
            >
              {t('common.edit')}
            </Button>
          )}
          {setting.savedValue !== null && !editing ? (
            <Button size="sm" variant="ghost" loading={busy} onClick={() => void run(() => onReset(setting.key))}>
              {t('config.reset')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
});

function Detail({ term, value }: { term: string; value: string }): ReactElement {
  return (
    <div className={styles.detail}>
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function SettingEditor({ setting, draft, onChange, disabled }: { setting: SettingDto; draft: string | boolean; onChange(value: string | boolean): void; disabled: boolean }): ReactElement {
  const { t, locale } = useT();
  const label = splitDocRefs(setting.description[locale]).text;
  if (setting.type === ParamType.Boolean) {
    return <Checkbox label={t('config.enabled')} checked={draft === true} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
  }
  if (setting.type === ParamType.Enum) {
    return <BareSelect label={label} value={String(draft)} disabled={disabled} onChange={(e) => onChange(e.target.value)} options={(setting.values ?? []).map((value) => ({ value, label: value }))} />;
  }
  const numeric = setting.type === ParamType.Integer || setting.type === ParamType.Number;
  return <BareInput label={label} value={String(draft)} disabled={disabled} inputMode={numeric ? 'decimal' : undefined} autoFocus onChange={(e) => onChange(e.target.value)} />;
}
